/*
 * Project: bellcloud
 * File: apps/frontend/src/components/dialogs/LogRequestDialog.tsx
 * Author: SWAN
 * Created: 2025-11-13
 * Description: 로그 요청 다이얼로그 (Cradle/Bluetooth 선택, FileType 선택, days 입력)
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui';
import { FileType } from '@packages/types/device/lite/file_type';
import { MiniFileType } from '@packages/types/device/mini/file_type';
import { TransferMode } from '@packages/types/device/lite/transfer_mode';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import type { LiteLogActiveConnectionType } from '@/types/connection';

interface LogRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (params: LogRequestParams) => void;
  deviceName?: string;
  deviceType?: DeviceType;
  currentConnectionType?: LiteLogActiveConnectionType; // 현재 연결 타입 (자동 감지용)
}

export interface LogRequestParams {
  connectionType: LiteLogActiveConnectionType;
  file_type?: FileType | MiniFileType; // 단일 요청
  file_types?: number[]; // datalog 방식: 다중 요청
  days_of_gas_reading?: number; // MINI는 필수 아님
  transfer_mode: TransferMode;
}

const LogRequestDialog: React.FC<LogRequestDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  deviceType = DeviceType.LITE,
  currentConnectionType, // 현재 연결 타입
}) => {
  const { theme } = useTheme();
  const isMini = deviceType === DeviceType.MINI;

  // MINI는 항상 bluetooth, LITE는 현재 연결 타입 또는 기본값 cradle
  const [connectionType, setConnectionType] = useState<LiteLogActiveConnectionType>(
    isMini ? 'bluetooth' : currentConnectionType || 'cradle'
  );
  const { t } = useTranslation();
  // LITE: 전체 로그 다운로드 (120일), MINI: days 파라미터 사용 안 함
  const days = 120;

  // 안내 화면 state (현재는 사용하지 않음 - 바로 다운로드)
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState<{ min: number; max: number } | null>(null);

  // 로그 타입 선택 (LITE)
  // Configuration Log를 위한 특수 키 사용 (99는 General Config(2)와 Sensor Config(3)를 묶음)
  // LITE: 전체 로그 다운로드 (모든 로그 타입 선택)
  const selectedLiteLogTypes = new Set([0, 1, 99, 4, 5]); // 전체 로그: Gas Reading, Event Log, Configuration Log, Gas Alarm Log, Calibration Bump Log

  // 로그 타입 선택 (MINI)
  // MINI: 전체 로그 다운로드 (모든 로그 타입 선택)
  const selectedMiniLogTypes = new Set([0, 1, 2]); // 전체 로그: Datalog, Eventlog, Cal_Bump

  // deviceType 또는 currentConnectionType이 변경될 때 connectionType 업데이트
  useEffect(() => {
    setConnectionType(isMini ? 'bluetooth' : currentConnectionType || 'cradle');
  }, [isMini, currentConnectionType]);

  // 모달이 열릴 때마다 안내 화면 state 리셋
  useEffect(() => {
    if (isOpen) {
      setShowConfirmation(false);
      setEstimatedTime(null);
    }
  }, [isOpen]);

  const [transferMode] = useState<TransferMode>(TransferMode.BURST);

  if (!isOpen) return null;

  // 예상 다운로드 시간 계산 함수 (현재는 사용하지 않음 - 바로 다운로드)
  // const GAS_DOWNLOAD_TIME_ESTIMATE: Record<number, { min: number; max: number }> = {
  //   30: { min: 3, max: 4 },
  //   40: { min: 4, max: 5 },
  //   50: { min: 4, max: 6 },
  //   60: { min: 5, max: 6 },
  //   70: { min: 6, max: 7 },
  //   80: { min: 6, max: 8 },
  //   90: { min: 7, max: 9 },
  //   100: { min: 8, max: 9 },
  //   110: { min: 8, max: 10 },
  //   120: { min: 9, max: 11 },
  // };

  // const estimateDownloadTime = (days: number): { min: number; max: number } | null => {
  //   if (days < 30) return null;

  //   const keys = Object.keys(GAS_DOWNLOAD_TIME_ESTIMATE).map(Number).sort((a, b) => a - b);

  //   if (days >= 120) {
  //     return GAS_DOWNLOAD_TIME_ESTIMATE[120];
  //   }

  //   for (let i = 0; i < keys.length - 1; i++) {
  //     const lower = keys[i];
  //     const upper = keys[i + 1];

  //     if (days >= lower && days < upper) {
  //       const lowerEstimate = GAS_DOWNLOAD_TIME_ESTIMATE[lower];
  //       const upperEstimate = GAS_DOWNLOAD_TIME_ESTIMATE[upper];
  //       const ratio = (days - lower) / (upper - lower);

  //       return {
  //         min: Math.round(lowerEstimate.min + (upperEstimate.min - lowerEstimate.min) * ratio),
  //         max: Math.round(lowerEstimate.max + (upperEstimate.max - lowerEstimate.max) * ratio),
  //       };
  //     }
  //   }

  //   return GAS_DOWNLOAD_TIME_ESTIMATE[30];
  // };

  const handleConfirm = () => {
    // LITE: Configuration Log(99)가 선택된 경우 General Config(2)와 Sensor Config(3)로 확장
    const expandLiteLogTypes = (types: Set<number>): number[] => {
      const result: number[] = [];
      types.forEach(type => {
        if (type === 99) {
          // Configuration Log는 General Config(2)와 Sensor Config(3)로 확장
          result.push(2, 3);
        } else {
          result.push(type);
        }
      });
      return result.sort((a, b) => a - b);
    };

    // 안내 화면에서 Confirm 버튼 클릭 시 실제 다운로드 시작
    if (showConfirmation) {
      const params: LogRequestParams = {
        connectionType,
        transfer_mode: transferMode,
      };

      // MINI: 전체 로그 다운로드 (days 파라미터 없음)
      if (isMini) {
        params.file_types = Array.from(selectedMiniLogTypes).sort((a, b) => a - b);
      }
      // LITE: 전체 로그 다운로드 (120일, Configuration Log 확장 처리)
      else {
        params.file_types = expandLiteLogTypes(selectedLiteLogTypes);
        params.days_of_gas_reading = days;
      }

      onConfirm(params);
      onClose();
      return;
    }

    // MINI/LITE: 전체 로그 다운로드로 변경 - 안내 화면 없이 바로 다운로드
    // 첫 번째 클릭: Gas Reading 30일 이상이면 안내 화면으로 전환
    // if (!isMini && selectedLiteLogTypes.has(0) && days >= 30) {
    //   const timeEstimate = estimateDownloadTime(days);
    //   if (timeEstimate) {
    //     setEstimatedTime(timeEstimate);
    //     setShowConfirmation(true);
    //     return;
    //   }
    // }

    // 바로 다운로드
    const params: LogRequestParams = {
      connectionType,
      transfer_mode: transferMode,
    };

    // MINI: 전체 로그 다운로드 (days 파라미터 없음)
    if (isMini) {
      params.file_types = Array.from(selectedMiniLogTypes).sort((a, b) => a - b);
    }
    // LITE: 전체 로그 다운로드 (120일, Configuration Log 확장 처리)
    else {
      params.file_types = expandLiteLogTypes(selectedLiteLogTypes);
      params.days_of_gas_reading = days;
    }

    onConfirm(params);
    onClose();
  };

  // 로그 타입 토글 핸들러 (현재는 사용하지 않음 - 전체 로그 다운로드)
  // const toggleLogType = (logType: number) => {
  //   if (isMini) {
  //     const newSet = new Set(selectedMiniLogTypes);

  //     // MINI도 Datalog(0)는 단독으로만
  //     if (logType === 0) {
  //       // Gas Reading(Datalog) 선택 시 다른 모든 로그 해제
  //       if (!newSet.has(0)) {
  //         newSet.clear();
  //         newSet.add(0);
  //       } else {
  //         newSet.delete(0);
  //       }
  //     } else {
  //       // 다른 로그 선택 시 Gas Reading 해제
  //       newSet.delete(0);
  //       if (newSet.has(logType)) {
  //         newSet.delete(logType);
  //       } else {
  //         newSet.add(logType);
  //       }
  //     }

  //     setSelectedMiniLogTypes(newSet);
  //   } else {
  //     const newSet = new Set(selectedLiteLogTypes);

  //     // LITE: Gas Reading(0)은 단독으로만
  //     if (logType === 0) {
  //       // Gas Reading 선택 시 다른 모든 로그 해제
  //       if (!newSet.has(0)) {
  //         newSet.clear();
  //         newSet.add(0);
  //       } else {
  //         newSet.delete(0);
  //       }
  //     } else {
  //       // 다른 로그 선택 시 Gas Reading 해제
  //       newSet.delete(0);
  //       if (newSet.has(logType)) {
  //         newSet.delete(logType);
  //       } else {
  //         newSet.add(logType);
  //       }
  //     }

  //     setSelectedLiteLogTypes(newSet);
  //   }
  // };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: theme.colors.surface.layout.overlay,
          borderRadius: theme.borderRadius.lg,
          padding: theme.spacing[8],
          width: '90%',
          maxWidth: '500px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            marginBottom: theme.spacing[7],
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: theme.fontSize.xl,
              color: theme.colors.text.primary,
              fontWeight: 600,
            }}
          >
            {showConfirmation
              ? t(i18nKeys.datalog.logRequestEstimatedDurationTitle)
              : t(i18nKeys.datalog.downloadLog)}
          </h2>
        </div>

        {/* 안내 화면 */}
        {showConfirmation && estimatedTime && (
          <div
            style={{
              padding: theme.spacing[8],
              backgroundColor: theme.colors.surface.layout.background,
              border: `1px solid ${theme.colors.border.container}`,
              borderRadius: theme.borderRadius.md,
              marginBottom: theme.spacing[7],
              textAlign: 'center',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: theme.fontSize.lg,
                color: theme.colors.text.primary,
                lineHeight: '1.6',
              }}
            >
              {t(i18nKeys.datalog.downloadTimeEstimate, {
                minMinutes: estimatedTime.min,
                maxMinutes: estimatedTime.max,
              })}
            </p>
            <p
              style={{
                margin: `${theme.spacing[6]} 0 0 0`,
                fontSize: theme.fontSize.sm,
                color: theme.colors.text.secondary,
              }}
            >
              {t(i18nKeys.datalog.logRequestContinuePrompt)}
            </p>
          </div>
        )}

        {/* MINI/LITE: 전체 로그 다운로드 안내 */}
        {!showConfirmation && (
          <div
            style={{
              padding: theme.spacing[8],
              backgroundColor: theme.colors.surface.layout.background,
              border: `1px solid ${theme.colors.border.container}`,
              borderRadius: theme.borderRadius.md,
              marginBottom: theme.spacing[7],
              textAlign: 'center',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: theme.fontSize.lg,
                color: theme.colors.text.primary,
                lineHeight: '1.6',
                whiteSpace: 'pre-line',
              }}
            >
              {isMini
                ? t(i18nKeys.datalog.logRequestFullLogDownloadDescription)
                : t(i18nKeys.datalog.logRequestFullLogDownloadBodyLite, { minutes: 10 })}
            </p>
          </div>
        )}

        {/* 버튼 */}
        <div
          style={{
            display: 'flex',
            gap: theme.spacing[6],
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={() => {
              if (showConfirmation) {
                // 안내 화면에서 뒤로 가기
                setShowConfirmation(false);
                setEstimatedTime(null);
              } else {
                // 모달 닫기
                onClose();
              }
            }}
            variant="secondary"
            width="100px"
          >
            {t(i18nKeys.datalog.cancel)}
          </Button>
          <Button onClick={handleConfirm} variant="primary" width="140px">
            {showConfirmation ? t(i18nKeys.config.confirm) : t(i18nKeys.datalog.downloadLog)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LogRequestDialog;
